import { useState, useMemo, useCallback } from 'react';
import moment from 'moment-timezone';
import {
  MobileDateRangePicker,
  MultiInputDateRangeField,
} from '@mui/x-date-pickers-pro';

import useGetLocaleTextDatePicker from 'src/hooks/useGetLocaleTextDatePicker';
import useAuth from './useAuth';

export const formatDatesFromRangeSelector = ({ range }) => {
  const [startRaw, endRaw] = range || [null, null];
  let start = startRaw;
  let end = endRaw;

  if (!start && end) {
    start = moment(end).subtract(1, 'week').toDate();
  }
  if (!end && start) {
    end = moment(start).add(1, 'week').toDate();
  }
  return { start, end };
};

const useDateFilterForTable = ({
  defaultDateRange,
  shouldDisableDate = false,
}) => {
  defaultDateRange = defaultDateRange ?? {
    start: null,
    end: null,
  };
  const { user } = useAuth();
  const [filterModel, setFilterModel] = useState({
    items: [],
    linkOperator: 'and',
  });
  const [dateRange, setDateRange] = useState(defaultDateRange);
  const localeText = useGetLocaleTextDatePicker();
  const filterModelWithDateRange = useMemo(
    () => ({
      items:
        dateRange.start && dateRange.end
          ? filterModel.items.concat([
              {
                id: 'date-filter-start',
                field: 'start_date',
                operator: 'onOrAfter',
                value: moment
                  .tz(
                    moment(dateRange.start).format('YYYY-MM-DD'),
                    user.timezone_id
                  )
                  .startOf('day')
                  .utc()
                  .format('YYYY-MM-DDTHH:mm'),
              },
              {
                id: 'date-filter-end',
                field: 'end_date',
                operator: 'onOrBefore',
                value: moment
                  .tz(
                    moment(dateRange.end).format('YYYY-MM-DD'),
                    user.timezone_id
                  )
                  .endOf('day')
                  .utc()
                  .format('YYYY-MM-DDTHH:mm'),
              },
            ])
          : filterModel.items,
      linkOperator: 'and',
    }),
    [filterModel.items, dateRange, user.timezone_id]
  );

  const updateDateRange = useCallback((range) => {
    setDateRange(formatDatesFromRangeSelector({ range }));
  }, []);

  const pickerKey = `${dateRange.start?.getTime() ?? 'null'}-${
    dateRange.end?.getTime() ?? 'null'
  }`;

  const DateRangeSelector = (
    <MobileDateRangePicker
      key={pickerKey}
      defaultValue={[dateRange.start, dateRange.end]}
      localeText={localeText}
      shouldDisableDate={(date) =>
        shouldDisableDate
          ? moment(date).valueOf() < moment().subtract(1, 'year').valueOf()
          : false
      }
      onAccept={updateDateRange}
      slots={{ field: MultiInputDateRangeField }}
      slotProps={{
        textField: { size: 'small' },
      }}
    />
  );

  return {
    filterModelWithDateRange,
    filterModel,
    setFilterModel,
    DateRangeSelector,
  };
};

export default useDateFilterForTable;
